<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomTextArea from '~/components/UI/Forms/CustomTextArea.vue';
  import { get, post } from '~/services/donationScreen';
  import type { GetResponse, ContactTypeDrawer, ResponseStatus, PostResponse } from '~/types';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();

  const action = computed(() => {
    return route.query.action as string;
  });

  const formRef = ref();
  const formState = reactive({
    contactTypeName: '',
    contactTypeCategory: '',
    description: ''
  });
  const status = ref<boolean>(false);

  const responseState = ref<ResponseStatus>('idle');
  const data = ref<ContactTypeDrawer>({} as ContactTypeDrawer);

  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  const fetchData = async () => {
    responseState.value = 'pending';
    try {
      const response = await get<GetResponse<ContactTypeDrawer>>(`v1/get_donor_type/${props.identifier}`);
      status.value = response?.data?.data?.selected_donor_type?.is_disabled;
      if (response.status === 200) {
        data.value = response.data.data;
        Object.assign(formState, {
          contactTypeName: response?.data?.data?.selected_donor_type?.donor_type_name || '',
          contactTypeCategory: response?.data?.data?.selected_donor_type?.donor_type_category || '',
          description: response?.data?.data?.selected_donor_type?.donor_type_description || ''
        });
        responseState.value = 'success';
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      responseState.value = 'error';
    }
  };

  const rules = {
    contactTypeName: [
      {
        required: true,
        message: 'Contact Type Name is required',
        trigger: 'submit'
      }
    ],
    contactTypeCategory: [
      {
        required: true,
        message: 'Contact Type Category is required',
        trigger: 'submit'
      }
    ],
    description: []
  };

  const updateStateField = (value: string, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
  };

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<ContactTypeDrawer>>(`v1/donor_type/${props.identifier}`, {
        data: {
          donor_type_name: formState.contactTypeName,
          donor_type_category: formState.contactTypeCategory,
          donor_type_description: formState.description,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Donor Type Updated Successfully');
        isModalVisible.value = false;
        props.onClose();
      } else if (response?.data?.status === 'error') {
        openFailedCustomNotification(response?.data?.message);
        isModalVisible.value = false;
        props.onClose();
      } else {
        openFailedCustomNotification('Failed to Update Donor Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Update Donor Type');
    }
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<ContactTypeDrawer>>('v1/add_donor_type', {
        data: {
          donor_type_name: formState.contactTypeName,
          donor_type_category: formState.contactTypeCategory,
          donor_type_description: formState.description
        }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Donor Type Created Successfully');
        editStatus.value = 'success';
        props.onClose();
      } else if (response.data.status === 'error') {
        editStatus.value = 'error';
        return openFailedCustomNotification(response.data.message || 'Failed to Create Donor Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Create Donor Type');
      editStatus.value = 'error';
    }
  };

  const handleCreateContactType = async () => {
    try {
      await formRef.value.validate();

      if (route.query.action === 'edit') {
        return openModal();
      }

      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');

      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const openDisableModal = async () => {
    try {
      isModalVisibleForDisable.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleDisableDonorType = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/donor_type_disabled/${props.identifier}`, {
        data: {
          donor_type_name: formState.contactTypeName,
          donor_type_category: formState.contactTypeCategory,
          donor_type_description: formState.description,
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Donor Type Disabled Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Disable Donor Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Disable Donor Type');
      editStatus.value = 'error';
    }
  };

  const handleActiveCostCenter = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/donor_type_enabled/${props.identifier}`, {
        data: {
          is_disabled: false
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Donor Type Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Donor Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Donor Type');
      editStatus.value = 'error';
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  onMounted(() => {
    if (props.identifier) {
      fetchData();
    }
  });
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'donorTypes'"
    :title="
      action === 'edit'
        ? 'Update Donor Type'
        : action === 'add' || action === 'duplicate'
          ? 'Add New Donor Type'
          : 'Donor Type'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <RoleBasedButton
          v-if="!props.identifier || action"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded sm-medium duration-150 border border-blue-600"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleCreateContactType"
        >
          {{ action === 'edit' ? 'Update Donor Type' : 'Create Donor Type' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="openDisableModal"
        >
          {{ 'Disable Donor Type' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActiveCostCenter"
        >
          {{ 'Active Donor Type' }}
        </RoleBasedButton>
      </div>
    </template>
    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '183px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomInput
        label-text="Donor Type Name"
        name="contactTypeName"
        :value="formState.contactTypeName"
        state-key="contactTypeName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="[
          { title: 'Individual', value: 'Individual' },
          { title: 'Corporate', value: 'Corporate' },
          { title: 'Foundation', value: 'Foundation' },
          { title: 'Government', value: 'Government' },
          { title: 'Anonymous', value: 'Anonymous' },
          { title: 'NGO', value: 'NGO' },
          { title: 'Others', value: 'Others' }
        ]"
        label-text="Donor Type Category"
        name="contactTypeCategory"
        :value="formState.contactTypeCategory"
        state-key="contactTypeCategory"
        :rule="rules.contactTypeCategory"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomTextArea
        label="Description"
        name="description"
        :value="formState.description"
        state-key="description"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <AuditLogs v-if="responseState === 'success'" :items="data?.audit_log_data" title="Donor Type" />
    </a-form>
    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Donor Type"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Disable Donor Type"
      :warning-message="'Are you sure you want to Disable this Donor Type?'"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisableDonorType"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
